import React from 'react';
import { FlexContainer } from 'components/Containers/FlexContainer/FlexContainer';
import { WideOfferApplyButton } from '../fragments/OfferApplyButtons/WideOfferApplyButton';
import { Row } from 'components/Containers/Row/Row';
import SectionContainer from 'components/Containers/SectionContainer/index';
import { CSTooltip } from 'components/Tooltip/CSToolTip';
import classnames from 'classnames';
import { get } from 'helpers/objectHelpers';
import { formatApr, formatNumberNoDecimals } from 'helpers/number';
import { formatUSD } from 'helpers/money.helper';
import { CREDIT_CARD_VERTICAL } from 'types/marketplaceTiles.constants';
import Label from 'components/form/Label/Label';
import { getLoanTermInMonths } from 'partial/OfferManager/modules/HomeLoanMarketplaceModule/helpers/homeLoan.helpers';

const DetailItem = props => {
  const { name, tooltip, value, info } = props;
  return (
    <>
      <FlexContainer className="format25 mformat100 padTB8 flex-column gap-4 m-gap-0">
        <FlexContainer className="flex-column flex-tablet-row m-justify-between m-items-align-center gap-4 m-gap-0">
          <div className="text-12 fontGray400">
            {name}
            {tooltip && (
              <CSTooltip
                isInline
                title={tooltip}
                vertical={CREDIT_CARD_VERTICAL}
                placement="bottom"
                overlayClassName="blueTooltip"
                arrowPointAtCenter
              />
            )}
          </div>
          <div className="text-20 m-text-16 bold fontGray500">{value}</div>
        </FlexContainer>
        {info && <div className="text-12">{info}</div>}
      </FlexContainer>
    </>
  );
};

const DetailsView = props => {
  const monthlyPayments = get(props.offerMetadata, [
    'bestProduct',
    'monthlyPayment',
  ]);

  const yearCode = props?.offerMetadata?.bestProduct?.code;
  const loanTermInMonths = getLoanTermInMonths(yearCode);

  if (monthlyPayments === null) {
    return '';
  }
  return (
    <>
      <div className="borderColorGray025 borderTp format100 margT16 margB16 t-hide">
        &nbsp;
      </div>
      <FlexContainer
        className={classnames(
          'format100 flex-row flex-tablet-col',
          props.className,
        )}
      >
        <DetailItem
          name="Estimated Mo. Payments"
          value={formatUSD(formatNumberNoDecimals(monthlyPayments))}
          info={`${loanTermInMonths} equal monthly payments`}
          tooltip={'Monthly payments do not include taxes and insurance.'}
        />
        <DetailItem
          name="Loan Type"
          value={get(props.offerMetadata, ['bestProduct', 'displayName'])}
        />
        <DetailItem
          name="Interest Rate"
          value={
            formatApr(get(props.offerMetadata, ['bestProduct', 'rate'])) + '%'
          }
          tooltip={props.offerMetadata.interestRateDisclosure}
        />
        <DetailItem
          name="APR"
          value={
            formatApr(get(props.offerMetadata, ['bestProduct', 'apr'])) + '%'
          }
          tooltip={props.offerMetadata.aprDisclosure}
        />
      </FlexContainer>
    </>
  );
};

const HomeLoanOfferNameAndInfo = ({ datatestid, offerMetadata, seenPixel }) => {
  return (
    <FlexContainer className="flex-column padR24 mobilePadR0">
      {seenPixel}
      <div className="d-hide">
        <DetailsView offerMetadata={offerMetadata} />
      </div>
    </FlexContainer>
  );
};

const DetailedHomeLoanOffer = ({
  datatestid,
  offerMetadata,
  clickApplyHandler,
  clickApplyLoanNameHandler,
  clickApplyImageHandler,
  seenPixel,
}) => {
  const offerDetails = offerMetadata.bestProduct;
  const nmls = offerDetails?.additionalLenderData?.nmls;
  const stateLicenceNumber =
    offerDetails?.additionalLenderData?.stateLicenceNumber;

  const formattedText =
    (nmls ? `NMLS #${nmls}` : '') +
    (nmls && stateLicenceNumber ? ' | ' : '') +
    (stateLicenceNumber ? `State Lic: ${stateLicenceNumber}` : '');

  return (
    <SectionContainer className="DetailedHomeLoanOffer grayShadow offerCard">
      <Row className="offerCardRow">
        <FlexContainer className="align-items-center flex-mobile-col">
          <FlexContainer column className="m-padB16">
            <img
              data-testid={`${datatestid}-hl-offer-image`}
              className="homeLoanOfferImage padR24 mobilePadR0"
              src={offerDetails.lender.logoUrl}
              style={{ maxWidth: 200 }}
              alt="Home Loan Offer"
              onClick={clickApplyImageHandler}
            ></img>
            {formattedText && (
              <Label className="text-12 padT12 m-padT18 m-text-center">
                {formattedText}
              </Label>
            )}
          </FlexContainer>
          <FlexContainer className="justify-between format100 flex-mobile-col">
            <HomeLoanOfferNameAndInfo
              datatestid={`${datatestid}-hl-offer`}
              offerMetadata={offerMetadata}
              seenPixel={seenPixel}
              clickApplyLoanNameHandler={clickApplyLoanNameHandler}
            />
            <WideOfferApplyButton
              containerClassName="padT0 m-padT16"
              datatestid={`${datatestid}-offer`}
              partnerName={offerMetadata.ctaDisclosure}
              clickApplyHandler={clickApplyHandler}
              isFullPartnerName
              hideIcon
            />
          </FlexContainer>
        </FlexContainer>
        <div className="t-hide format100">
          <DetailsView offerMetadata={offerMetadata} />
        </div>
      </Row>
    </SectionContainer>
  );
};

export { DetailedHomeLoanOffer };
